import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AudioLines, Mic, Music, Download, Trash2, Loader2, Upload, Sparkles, Plug, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import LeftRail from "@/components/LeftRail";
import {
  listVoices, voiceQuota, tts, sfx, cloneVoice, voiceHistory, deleteVoice, getConnections,
} from "@/lib/falApi";

const cls = (...a) => a.filter(Boolean).join(" ");
const MODES = [["tts", Mic, "Text to Speech"], ["sfx", Music, "Sound FX"], ["clone", AudioLines, "Voice Clone"]];

export default function Voice() {
  const [mode, setMode] = useState("tts");
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState("");
  const [text, setText] = useState("");
  const [stability, setStability] = useState(0.5);
  const [similarity, setSimilarity] = useState(0.75);
  const [style, setStyle] = useState(0.0);
  const [duration, setDuration] = useState(0);
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState(null);
  const [history, setHistory] = useState([]);
  const [quota, setQuota] = useState(null);
  const [needsKey, setNeedsKey] = useState(false);
  const [checking, setChecking] = useState(true);
  const [cloneName, setCloneName] = useState("");
  const [cloneFiles, setCloneFiles] = useState([]);

  useEffect(() => { }, []);

  const loadVoices = useCallback(async () => {
    try {
      const v = await listVoices();
      setVoices(v);
      if (v[0]) setVoiceId((cur) => cur || v[0].voice_id);
      setNeedsKey(false);
    } catch (e) {
      if ([400, 401].includes(e?.response?.status)) setNeedsKey(true);
    }
  }, []);
  const loadHistory = useCallback(async () => { try { setHistory(await voiceHistory()); } catch (e) { console.error("Failed to load voice history", e); } }, []);

  useEffect(() => {
    document.documentElement.classList.add("dark");
    (async () => {
      try {
        const c = await getConnections();
        if (!c.elevenlabs) { setNeedsKey(true); setChecking(false); return; }
      } catch (e) { console.error("Failed to check connections", e); }
      loadVoices(); loadHistory();
      voiceQuota().then(setQuota).catch(() => {});
      setChecking(false);
    })();
  }, [loadVoices, loadHistory]);

  const selectedVoice = voices.find((v) => v.voice_id === voiceId);

  const run = async () => {
    setBusy(true); setOutput(null);
    try {
      let rec;
      if (mode === "tts") {
        if (!text.trim()) { toast.error("Type something to speak"); setBusy(false); return; }
        rec = await tts({ text, voice_id: voiceId, voice_name: selectedVoice?.name, stability, similarity_boost: similarity, style });
      } else {
        if (!text.trim()) { toast.error("Describe the sound"); setBusy(false); return; }
        rec = await sfx({ text, duration: duration || undefined });
      }
      setOutput(rec);
      toast.success(mode === "tts" ? "Speech generated" : "Sound generated");
      loadHistory();
      voiceQuota().then(setQuota).catch(() => {});
    } catch (e) {
      const d = e?.response?.data?.detail || "Generation failed";
      toast.error(d);
      if ([400, 401].includes(e?.response?.status)) setNeedsKey(true);
    } finally { setBusy(false); }
  };

  const runClone = async () => {
    if (!cloneName.trim() || !cloneFiles.length) { toast.error("Add a name and at least one audio sample"); return; }
    setBusy(true);
    try {
      const res = await cloneVoice(cloneName, "", cloneFiles);
      toast.success(`Voice "${res.name}" cloned`);
      setCloneName(""); setCloneFiles([]);
      await loadVoices();
      setVoiceId(res.voice_id); setMode("tts");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
      if ([400, 401].includes(e?.response?.status)) setNeedsKey(true);
    } finally { setBusy(false); }
  };

  const removeHistory = async (id) => { await deleteVoice(id).catch(() => {}); loadHistory(); };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex" data-testid="voice-page">
        <LeftRail />
        <div className="flex-1 grid place-items-center"><Loader2 className="h-6 w-6 text-[#faff69] animate-spin" /></div>
      </div>
    );
  }

  if (needsKey) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex" data-testid="voice-page">
        <LeftRail />
        <div className="flex-1 grid place-items-center p-6">
          <div className="text-center max-w-sm">
            <div className="h-12 w-12 rounded-xl bg-[#faff69]/10 grid place-items-center mx-auto"><AudioLines className="h-6 w-6 text-[#faff69]" /></div>
            <h2 className="font-display font-bold text-xl mt-4">Connect ElevenLabs</h2>
            <p className="text-sm text-zinc-400 mt-2">Add your ElevenLabs API key to use text-to-speech, sound effects and voice cloning.</p>
            <Link to="/connections"><Button className="mt-5 bg-[#faff69] hover:bg-[#e6eb52] text-black" data-testid="voice-connect-btn"><Plug className="h-4 w-4 mr-1.5" /> Open Connections</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex" data-testid="voice-page">
      <LeftRail />
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 border-b border-zinc-800 flex items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <AudioLines className="h-5 w-5 text-[#faff69]" />
            <h1 className="font-display font-bold text-lg tracking-tight">Voice Studio</h1>
            <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em]">ElevenLabs</span>
          </div>
          {quota?.available && <span className="font-mono text-[11px] text-zinc-400" data-testid="voice-quota">{quota.remaining.toLocaleString()} chars left</span>}
        </header>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-0">
          <aside className="border-b lg:border-b-0 lg:border-r border-zinc-800 p-4 space-y-2">
            {MODES.map(([m, Icon, label]) => (
              <button key={m} onClick={() => { setMode(m); setOutput(null); }} data-testid={`voice-mode-${m}`}
                className={cls("w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  mode === m ? "bg-[#faff69] text-black" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60")}>
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </aside>

          <main className="overflow-y-auto p-6">
            <div className="max-w-2xl mx-auto space-y-5">
              {mode === "clone" ? (
                <>
                  <div>
                    <label className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Voice name</label>
                    <Input data-testid="clone-name" value={cloneName} onChange={(e) => setCloneName(e.target.value)} placeholder="e.g. My Narrator"
                      className="mt-2 bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Audio samples</label>
                    <div onClick={() => document.getElementById("clone-input").click()} data-testid="clone-dropzone"
                      className="mt-2 rounded-lg border-2 border-dashed border-zinc-700 hover:border-zinc-600 bg-zinc-900/40 p-6 text-center cursor-pointer">
                      <input id="clone-input" type="file" accept="audio/*" multiple className="hidden"
                        onChange={(e) => setCloneFiles(Array.from(e.target.files))} />
                      <Upload className="h-6 w-6 mx-auto text-zinc-500" />
                      <p className="text-sm text-zinc-400 mt-2">Upload 1+ clean audio samples (mp3/wav)</p>
                    </div>
                    {cloneFiles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {cloneFiles.map((f, i) => (
                          <div key={`${f.name}-${f.size}-${f.lastModified}`} className="flex items-center justify-between text-xs text-zinc-400 bg-[#121212] border border-zinc-800 rounded px-2.5 py-1.5">
                            <span className="truncate">{f.name}</span>
                            <button onClick={() => setCloneFiles((p) => p.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button onClick={runClone} disabled={busy} data-testid="clone-submit" className="w-full py-6 text-base font-semibold bg-[#faff69] hover:bg-[#e6eb52] text-black">
                    {busy ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Cloning…</> : <><AudioLines className="h-5 w-5 mr-2" /> Create voice</>}
                  </Button>
                </>
              ) : (
                <>
                  {mode === "tts" && (
                    <div>
                      <label className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Voice</label>
                      <select data-testid="voice-select" value={voiceId} onChange={(e) => setVoiceId(e.target.value)}
                        className="mt-2 w-full bg-[#0a0a0a] border border-zinc-800 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#faff69] outline-none">
                        {voices.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}{v.category ? ` · ${v.category}` : ""}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">{mode === "tts" ? "Text" : "Sound description"}</label>
                    <Textarea data-testid="voice-text" value={text} onChange={(e) => setText(e.target.value)}
                      placeholder={mode === "tts" ? "Type what you want spoken…" : "e.g. thunder rumbling over a distant city, cinematic"}
                      className="mt-2 min-h-[130px] bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69] text-sm resize-none" />
                  </div>

                  {mode === "tts" ? (
                    <div className="grid grid-cols-3 gap-4">
                      <VSlider label="Stability" value={stability} onChange={setStability} testid="stability" />
                      <VSlider label="Similarity" value={similarity} onChange={setSimilarity} testid="similarity" />
                      <VSlider label="Style" value={style} onChange={setStyle} testid="style" />
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-between mb-2"><span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Duration</span>
                        <span className="font-mono text-xs">{duration ? `${duration}s` : "auto"}</span></div>
                      <Slider value={[duration]} min={0} max={22} step={1} onValueChange={(v) => setDuration(v[0])} data-testid="sfx-duration" />
                    </div>
                  )}

                  <Button onClick={run} disabled={busy} data-testid="voice-generate" className="w-full py-6 text-base font-semibold bg-[#faff69] hover:bg-[#e6eb52] text-black">
                    {busy ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="h-5 w-5 mr-2" /> Generate</>}
                  </Button>
                </>
              )}

              {output && (
                <div className="rounded-lg border border-zinc-800 bg-[#121212] p-4 fade-in" data-testid="voice-output">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono text-[#faff69] uppercase tracking-[0.15em]">{output.type}</span>
                    <a href={output.audio_url} download={`${output.type}.mp3`} data-testid="voice-download" className="text-zinc-400 hover:text-white"><Download className="h-4 w-4" /></a>
                  </div>
                  <audio src={output.audio_url} controls className="w-full" data-testid="voice-audio" />
                  <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{output.text}</p>
                </div>
              )}

              <div className="pb-8">
                <label className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Voice history</label>
                {history.length ? (
                  <div className="mt-3 space-y-2">
                    {history.map((h) => (
                      <div key={h.id} className="rounded-lg border border-zinc-800 bg-[#121212] p-3" data-testid={`voice-history-${h.id}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-mono text-[#faff69] uppercase">{h.type}{h.voice_name ? ` · ${h.voice_name}` : ""}</span>
                          <button onClick={() => removeHistory(h.id)} data-testid={`voice-delete-${h.id}`} className="text-zinc-500 hover:text-[#FF3B30]"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                        <audio src={h.audio_url} controls className="w-full h-9" />
                        <p className="text-[11px] text-zinc-500 mt-1.5 line-clamp-1">{h.text}</p>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-zinc-600 mt-3">No voice renders yet.</p>}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function VSlider({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="flex justify-between mb-2"><span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400">{label}</span>
        <span className="font-mono text-xs">{value.toFixed(2)}</span></div>
      <Slider value={[value]} min={0} max={1} step={0.05} onValueChange={(v) => onChange(v[0])} data-testid={`${testid}-slider`} />
    </div>
  );
}
